/**
 * ClientAuthContext
 *
 * Client portal equivalent of AuthContext -- entirely separate state, own
 * endpoints (/api/client/auth/*), own cookie (bbh_client_session) handled
 * server-side. Never shares state with the admin AuthContext so the two
 * auth realms can't bleed into each other in the UI either.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClientPerson {
  full_name: string;
  linked_project_names: string[];
}

interface ClientAuthContextValue {
  person: ClientPerson | null;
  loading: boolean;
  login: (
    username: string,
    password: string,
    turnstileToken: string
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ClientAuthContext = createContext<ClientAuthContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ClientAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [person, setPerson] = useState<ClientPerson | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch('/api/client/auth/session');
      if (!res.ok) {
        setPerson(null);
        return;
      }
      const json = (await res.json()) as {
        success: boolean;
        data: { authenticated: boolean; person: ClientPerson | null };
      };
      if (json.success && json.data.authenticated && json.data.person) {
        setPerson(json.data.person);
      } else {
        setPerson(null);
      }
    } catch {
      setPerson(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(
    async (
      username: string,
      password: string,
      turnstileToken: string
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const res = await fetch('/api/client/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password, turnstile_token: turnstileToken }),
        });

        const json = (await res.json()) as {
          success: boolean;
          data?: ClientPerson;
          message?: string;
        };

        if (res.ok && json.success && json.data) {
          setPerson(json.data);
          return { success: true };
        }

        return { success: false, error: json.message ?? 'Sign in failed. Please try again.' };
      } catch {
        return { success: false, error: 'Network error. Please try again.' };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await fetch('/api/client/auth/logout', { method: 'POST' });
    } catch {
      // Ignore -- clear local state regardless
    }
    setPerson(null);
  }, []);

  return (
    <ClientAuthContext.Provider value={{ person, loading, login, logout }}>
      {children}
    </ClientAuthContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useClientAuth(): ClientAuthContextValue {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return ctx;
}
